import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Pane } from "src/app/components/Pane/Pane";
import {
  PaneTopbar,
  PaneTopbarLeft,
  PaneTopbarToolbar,
} from "src/app/components/Pane/PaneTopbar/PaneTopbar";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { Button } from "src/app/components/Button/Button";
import { ReactComponent as ArrowLeftIcon } from "src/images/angle-left-grey.svg";
import { PaneContainer } from "src/app/components/Pane/PaneContainer/PaneContainer";
import { RootState } from "src/redux/reducers";
import {
  clearMessagesForTask,
  getMessagesListForTask,
  setCurrentTaskId,
} from "src/redux";
import Loader from "src/app/components/Loader/Loader";
import XssFilter from "src/app/components/XssFilter/XssFilter";
import { useShowPane } from "src/hooks/useShowPane";
import UsernameAndAvatar from "src/app/components/UsernameAndAvatar/UsernameAndAvatar";
import { getImageSource } from "src/utils/methods";
import AvatarPlaceholder from "src/images/avatar-purple.svg";
import MessageDetailItem from "./MessageDetailItem";

import "./MessageHistoryPreview.scss";

const ALLOWED_TAGS = ["b", "i", "u", "br"];

interface MessageHistoryPreviewProps {
  handleClose: () => void;
}

export default function MessageHistoryPreview({
  handleClose,
}: MessageHistoryPreviewProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  const { messages, areMessagesLoading, currentTaskId } = useSelector(
    (state: RootState) => state.taskReducer,
  );

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const { membersList } = useSelector(
    (state: RootState) => state.projectReducer,
  );

  const { tasks } = useSelector((state: RootState) => state.taskReducer);

  const dispatch = useDispatch();

  const [showPane, hidePane] = useShowPane(() => {
    handleClose();
  });

  const currentMessageNumber = currentMessageIndex + 1;
  const isLastMessage =
    messages.length === 0 || currentMessageNumber === messages.length;
  const currentMessage =
    currentMessageNumber <= messages.length
      ? messages[currentMessageIndex]
      : null;

  const safeMessageContent = XssFilter(
    currentMessage?.content ?? "",
    ALLOWED_TAGS,
  );

  const sentToTasks = tasks.filter((task) =>
    currentMessage?.toTaskUuids.includes(task.taskId),
  );

  const sentToMember = membersList.find(
    (member) => member.id === currentMessage?.author,
  );

  useEffect(() => {
    if (!currentTaskId) return;
    dispatch(getMessagesListForTask(currentTaskId, activeWorkspaceUuid));
  }, [currentTaskId, activeWorkspaceUuid, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearMessagesForTask());
      dispatch(setCurrentTaskId(null));
    };
  }, [dispatch]);

  return (
    <Pane
      className="message-history-preview"
      showPane={showPane}
      hidePane={hidePane}
    >
      <PaneTopbar>
        <PaneTopbarLeft>
          <h1 className="text-h3-regular">
            <IDHFormattedMessage
              id="ws_message_details"
              defaultMessage="Message details"
            />
          </h1>
          {!areMessagesLoading && (
            <div className="message-history-preview__buttons">
              <Button
                variant="white-with-light-grey-border"
                disabled={currentMessageNumber === 1}
                onClick={() => setCurrentMessageIndex((prev) => prev - 1)}
              >
                <ArrowLeftIcon />
              </Button>
              <div className="message-history-preview__pagination">
                <span>{currentMessageNumber}</span>
                <span className="message-history-preview__pagination--separator">
                  /
                </span>
                <span>{messages.length}</span>
              </div>
              <Button
                variant="white-with-light-grey-border"
                disabled={isLastMessage || messages.length === 0}
                onClick={() => setCurrentMessageIndex((prev) => prev + 1)}
              >
                <ArrowLeftIcon className="message-history-preview__arrow-right" />
              </Button>
            </div>
          )}
        </PaneTopbarLeft>
        <PaneTopbarToolbar hidePane={hidePane} />
      </PaneTopbar>
      {areMessagesLoading ? (
        <div className="message-history-preview__loader">
          <Loader />
        </div>
      ) : (
        <PaneContainer noBorder>
          <ul className="message-history-preview__message-details">
            <MessageDetailItem labelId="ws_sent" labelDefault="Sent">
              <UsernameAndAvatar
                username={sentToMember?.name}
                avatarUrl={sentToMember?.avatarUrl ?? AvatarPlaceholder}
              />
            </MessageDetailItem>

            <MessageDetailItem labelId="ws_date" labelDefault="Date">
              {currentMessage?.sentAt}
            </MessageDetailItem>

            <MessageDetailItem labelId="ws_from" labelDefault="From">
              {currentMessage?.fromEmail}
            </MessageDetailItem>

            <MessageDetailItem labelId="ws_to" labelDefault="To">
              <div className="message-history-preview__side-by-side">
                {sentToTasks.length < 1
                  ? "-"
                  : sentToTasks.map((task) => (
                      <UsernameAndAvatar
                        key={task.taskId}
                        username={task.title}
                        avatarUrl={getImageSource(task.cover, "tiny")}
                      />
                    ))}
              </div>
            </MessageDetailItem>

            <MessageDetailItem labelId="ws_reply_to" labelDefault="Reply to">
              {currentMessage?.replyTo}
            </MessageDetailItem>

            <MessageDetailItem labelId="ws_subject" labelDefault="Subject">
              {currentMessage?.subject}
            </MessageDetailItem>

            <MessageDetailItem
              labelId="ws_message"
              labelDefault="Message"
              dangerousHTML={safeMessageContent}
            />
          </ul>
        </PaneContainer>
      )}
    </Pane>
  );
}
